import { type SEOHandle } from '@nasa-gcn/remix-seo'
import { type MetaFunction } from 'react-router'

export const handle: SEOHandle = {
	getSitemapEntries: () => [
		{ route: '/legal-notice/community-guidelines', priority: 0.7 },
	],
}

export const meta: MetaFunction = ({ location }) => {
	const title = `Community Guidelines | Legal Notice | ${import.meta.env.VITE_APP_TITLE}`
	const description = `Community Guidelines for ${import.meta.env.VITE_APP_TITLE} users`

	const url = `https://www.trailviewr.com${location.pathname}`

	return [
		{ title },
		{ name: 'description', content: description },
		{ tagName: 'link', rel: 'canonical', href: url },

		{ property: 'og:title', content: title },
		{ property: 'og:description', content: description },
		{ property: 'og:url', content: url },

		{ name: 'twitter:title', content: title },
		{ name: 'twitter:description', content: description },
		{ name: 'twitter:url', content: url },

		{
			'script:ld+json': {
				'@context': 'https://schema.org',
				'@type': 'WebPage',
				name: title,
				description,
				url,
			},
		},
	]
}

export default function CommunityGuidelines() {
	return (
		<main className="mx-auto max-w-4xl px-4 py-8">
			<h1 className="mb-6 text-3xl font-bold">COMMUNITY GUIDELINES</h1>
			<p className="mb-4">
				<strong className="mr-2">Last updated</strong>
				<strong>October 6, 2025</strong>
			</p>
			<p className="mb-4">
				Welcome to TrailViewr! We're a community of outdoor enthusiasts,
				explorers, and adventurers who share a passion for discovering new
				trails, whether on foot, bike, or even underwater. Our goal is to build
				a supportive, safe, and vibrant community where everyone can share their
				experiences and knowledge. To make this possible, we ask all members to
				adhere to these community guidelines.
			</p>

			<h2 className="mt-6 mb-4 text-2xl font-bold">WHAT WE DON'T ALLOW</h2>

			<h3 className="mb-2 font-bold">1. Hateful Content</h3>
			<p className="mb-4">
				No hateful content of any kind is allowed. This includes, but is not
				limited to, content that is racist, discriminatory based on ethnicity,
				gender identity, sexual orientation, age, disability, or any other
				characteristic. We stand for respect and equality.
			</p>

			<h3 className="mb-2 font-bold">2. Violence, Threats, and Bullying</h3>
			<p className="mb-4">
				Threats, bullying, harassment, or any form of harmful or violent content
				is strictly prohibited. This includes content that promotes harm to
				oneself or others. Let's keep our community uplifting and supportive.
			</p>

			<h3 className="mb-2 font-bold">3. Nudity or Sexually Explicit Content</h3>
			<p className="mb-4">
				TrailViewr is a platform for sharing your love of the outdoors. Nudity
				and sexually explicit content are not appropriate here and are not
				allowed in any form.
			</p>

			<h3 className="mb-2 font-bold">4. Misinformation</h3>
			<p className="mb-4">
				No spreading misinformation. TrailViewr is built on trust and reliable
				information. Posting inaccurate or misleading content is not permitted,
				especially when it comes to safety or trail conditions.
			</p>

			<h3 className="mb-2 font-bold">5. Spam</h3>
			<p className="mb-4">
				No spam allowed. This includes repetitive content, unsolicited
				promotions, or irrelevant posts that detract from the value of our
				community.
			</p>

			<h3 className="mb-2 font-bold">6. Doxing and Privacy Violations</h3>
			<p className="mb-4">
				Do not share personal information about others without their consent.
				Doxing or violating someone's privacy in any way is strictly prohibited.
			</p>

			<h3 className="mb-2 font-bold">7. Multiple Accounts and Impersonation</h3>
			<p className="mb-4">
				Operating multiple accounts, avoiding bans, using throwaway emails, or
				impersonating others is not allowed. TrailViewr thrives on genuine
				connections and integrity.
			</p>

			<h3 className="mb-2 font-bold">8. No Self-Promotion</h3>
			<p className="mb-4">
				We encourage you to share your experiences, but not for self-promotion
				purposes. Linking directly to social media accounts (Instagram, TikTok,
				YouTube, etc.) with the sole intention of driving views or followers is
				discouraged and may be removed. We're here to engage and connect, not to
				promote and run.
			</p>

			<h2 className="mt-6 mb-4 text-2xl font-bold">WHAT WE ENCOURAGE</h2>

			<h3 className="mb-2 font-bold">1. Helping Others</h3>
			<p className="mb-4">
				TrailViewr is a space where members can support one another. Share your
				knowledge about trails, safety tips, or your own experiences to help
				others navigate their outdoor adventures.
			</p>

			<h3 className="mb-2 font-bold">2. Promoting a Supportive Atmosphere</h3>
			<p className="mb-4">
				Be kind, considerate, and encouraging. Your words can have a positive
				impact, and we're all here to learn and explore together. Let's make
				sure everyone feels welcomed.
			</p>

			<h3 className="mb-2 font-bold">
				3. Cooperation to Maintain These Guidelines
			</h3>
			<p className="mb-4">
				We all share responsibility for keeping TrailViewr a great community. If
				you see something that doesn't align with these guidelines, please
				report it. Your cooperation ensures we all enjoy a safe, respectful, and
				fun environment.
			</p>

			<h2 className="mt-6 mb-4 text-2xl font-bold">
				REMEMBER: ACCURATE CONTENT IS KEY
			</h2>
			<p className="mb-4">
				TrailViewr is about mapping the outdoors, and providing accurate
				information is at the heart of our mission. Misinformation, whether
				intentional or accidental, can impact the experience and safety of
				others. Always ensure your posts are accurate and informative.
			</p>

			<h2 className="mt-6 mb-4 text-2xl font-bold">CONSEQUENCES</h2>
			<p className="mb-4">
				Failure to follow these guidelines can result in the removal of content,
				suspension, or permanent banning from TrailViewr. Let's work together to
				make this an enjoyable and valuable community for everyone.
			</p>

			<p className="mb-4">
				Thank you for being a part of TrailViewr and for helping make this
				community great! If you have any questions about these guidelines or
				need clarification, feel free to reach out to our support team. We
				appreciate your cooperation and enthusiasm!
			</p>
		</main>
	)
}
